function formatMoney(cents) {
  if (!cents) return "$0";
  let value = (cents / 100).toFixed(0);
  return "$" + value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";

          const productInfoNode = html.querySelector(selector);
          if (!productInfoNode) return;

          const variant = this.getSelectedVariant(productInfoNode);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const newTitle = html.querySelector("head title");
            const currentTitle = document.querySelector("head title");

            if (newTitle && currentTitle) {
              currentTitle.innerHTML = newTitle.innerHTML;
            }

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );

            callback(html);
          })
          .then(() => {
            if (targetId) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;

        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;

        if (!selectedVariant) return null;

        try {
          return JSON.parse(selectedVariant);
        } catch (error) {
          console.error("Invalid selected variant JSON", error);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
        }

        if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");

        if (variantSelects && this.variantSelectors) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");

          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );

          updateSourceFromDestination("Inventory", ({ innerText }) =>
            innerText === ""
          );

          updateSourceFromDestination("Volume");

          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);

          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;

          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));

          const productDataEl = document.querySelector(
            `#ProductJson-${this.dataset.section}`
          );
          if (!productDataEl) return;

          let productData;

          try {
            productData = JSON.parse(productDataEl.textContent);
          } catch (e) {
            console.error("Invalid product JSON", e);
            return;
          }

          const variant = productData?.variants?.find(
            (v) => String(v.id) === String(variantId)
          );

          if (!variant) return;

          const addToCartBtn = document.querySelector(
            `#product-form-${this.dataset.section} [type="submit"] .price-area`
          );

          if (!addToCartBtn) return;

          addToCartBtn.innerHTML = `<span class="add-to-cart-price">${formatMoney(
            variant.price
          )}</span>`;
        });
      }

      updateURL(url, variantId) {
        let collection = document
          .querySelector("[data-collection]")
          ?.getAttribute("data-collection");

        if (!collection) {
          const collectionMatch = window.location.pathname.match(
            /\/collections\/([^/]+)/
          );
          collection = collectionMatch ? collectionMatch[1] : null;
        }

        if (!collection) {
          collection = "";
        }

        const updatedUrl = collection
          ? `${window.shopUrl}/collections/${collection}${url}${
              variantId ? `?variant=${variantId}` : ""
            }`
          : `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`;

        this.querySelector("share-button")?.updateUrl(updatedUrl);

        if (this.dataset.updateUrl === "false") return;

        window.history.replaceState({}, "", updatedUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");

        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        /**************** product wishlist ****************/
        if (window.jQuery) {
          (function (Wishlist, $) {
            let $wishlistButtons = $(".wishlist-btn");
            let $wishlistRemoveButtons = $(".remove-wishlist");
            let $wishlistItemCount = $(".wishlist-item-count");
            let wishlist = localStorage.getItem("user_wishlist");

            wishlist = wishlist ? JSON.parse(wishlist) : [];

            function updateWishlistCount() {
              $wishlistItemCount.text(wishlist.length);
            }

            function animateWishlist(button) {
              $(button).toggleClass("included");
            }

            function updateWishlist(button) {
              let handle = $(button).attr("data-product-handle");
              let index = wishlist.indexOf(handle);

              if ($(button).hasClass("included")) {
                if (index > -1) wishlist.splice(index, 1);
              } else {
                if (index === -1) wishlist.push(handle);
              }

              localStorage.setItem("user_wishlist", JSON.stringify(wishlist));
            }

            function activateIncludedButtons() {
              $wishlistButtons.each(function () {
                let handle = $(this).attr("data-product-handle");

                if (wishlist.indexOf(handle) > -1) {
                  $(this).addClass("included");
                } else {
                  $(this).removeClass("included");
                }
              });
            }

            function bindEvents() {
              $wishlistButtons.off("click").on("click", function (e) {
                e.preventDefault();
                updateWishlist(this);
                animateWishlist(this);
                updateWishlistCount();
              });

              $wishlistRemoveButtons.off("click").on("click", function (e) {
                e.preventDefault();

                let handle = $(this).attr("data-product-handle");
                let index = wishlist.indexOf(handle);

                if (index > -1) wishlist.splice(index, 1);

                localStorage.setItem("user_wishlist", JSON.stringify(wishlist));
                updateWishlistCount();
                location.reload();
              });
            }

            Wishlist.init = function () {
              $wishlistButtons = $(".wishlist-btn");
              $wishlistRemoveButtons = $(".remove-wishlist");
              activateIncludedButtons();
              bindEvents();
              updateWishlistCount();
            };
          })((window.Wishlist = window.Wishlist || {}), jQuery);

          setTimeout(() => {
            window.Wishlist?.init?.();
          }, 100);
        }

        /**************** product images ****************/
        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector("media-gallery ul");

        if (mediaGallerySource && mediaGalleryDestination) {
          const refreshSourceData = () => {
            const mediaGallerySourceItems = Array.from(
              mediaGallerySource.querySelectorAll("li[data-media-id]")
            );

            const sourceSet = new Set(
              mediaGallerySourceItems.map((item) => item.dataset.mediaId)
            );

            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [
                item.dataset.mediaId,
                { item, index },
              ])
            );

            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();

          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );

          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );

          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) {
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();
          }

          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        const mediaGallery = this.querySelector("media-gallery");

        if (mediaGallery) {
          mediaGallery.refreshSwiper?.();

          window.setTimeout(() => {
            mediaGallery.setActiveMedia?.(
              `${this.dataset.section}-${variantFeaturedMediaId}`,
              true
            );

            mediaGallery.refreshSwiper?.();
            window.initAmazonFinalZoom?.();
          }, 300);
        }

        const modalContent = this.productModal?.querySelector(
          ".product-media-modal__content"
        );

        const newModalContent = html.querySelector(
          "product-modal .product-media-modal__content"
        );

        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }

        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        );

        spinner?.classList.remove("hidden");

        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );

            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            spinner?.classList.add("hidden");
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        if (!this.quantityForm) return;
        if (!html) return;

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );

        // Main fix: variant change પછી Quantity Form ના મળે તો JS break ના થાય
        if (!quantityFormUpdated) {
          return;
        }

        this.setQuantityBoundries();

        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);

              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector("product-form");
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector("pickup-availability");
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}